/**
 * Local (client-side) representation of a chat attachment. Each item in the
 * chat composer maps to one of these, regardless of whether the file is
 * still uploading, has succeeded, or hit an error we can retry past.
 *
 * Kept as a discriminated union so consumer code (chips, send handler,
 * tests) gets exhaustive checks instead of optional-everywhere fields.
 */
import type { AttachmentKind, AttachmentRead } from '@/api/attachments'

export type LocalAttachmentStatus =
  | 'pending'
  | 'uploading'
  | 'ready'
  | 'error'

export interface LocalAttachmentBase {
  /** Local-only id — survives the upload round-trip. */
  localId: string
  filename: string
  byteSize: number
  mimeType: string
  /**
   * Best-effort classification used purely for icon/colour selection
   * before the server tells us its own kind. For real screenshots this
   * is set to ``"screenshot"`` even though the server may classify as
   * ``"image"``.
   */
  kind: AttachmentKind
  /** ``true`` when the user added this via paste / screenshot capture. */
  isScreenshot: boolean
  /**
   * Object-URL pointer used to render an inline thumbnail while the file
   * is still uploading and we don't yet have a server URL. Created via
   * `URL.createObjectURL`; the hook is responsible for revoking it.
   */
  localPreviewUrl: string | null
}

export interface LocalAttachmentPending extends LocalAttachmentBase {
  status: 'pending'
}

export interface LocalAttachmentUploading extends LocalAttachmentBase {
  status: 'uploading'
  /** Set when the request actually starts so the cancel button can abort it. */
  abort: () => void
}

export interface LocalAttachmentReady extends LocalAttachmentBase {
  status: 'ready'
  /** What the server gave us back. */
  server: AttachmentRead
}

export interface LocalAttachmentError extends LocalAttachmentBase {
  status: 'error'
  /** User-readable error from upload — toast and chip both show this. */
  message: string
  /** ``true`` when we should NOT offer a retry (validation, auth, etc.). */
  permanent: boolean
}

export type LocalAttachment =
  | LocalAttachmentPending
  | LocalAttachmentUploading
  | LocalAttachmentReady
  | LocalAttachmentError

export function isReady(
  att: LocalAttachment,
): att is LocalAttachmentReady {
  return att.status === 'ready'
}

export function isBusy(att: LocalAttachment): boolean {
  return att.status === 'pending' || att.status === 'uploading'
}
